import api from './api';
import type {
  AdminStats,
  AdminPilotSummary,
  AdminUploadItem,
  AdminParsedEvent,
  AdminRuleTriggerLog,
  AdminEmailIngestion,
  OrganizationDetail,
  SiteListItem,
  SiteDetail,
  GuardListItem,
  GuardDetail,
  RuleItem,
  RuleDetail,
  RuleVersion,
  ScoringCycle,
  PaginatedResponse,
  PatrolServicePlan,
  Pilot2SimulationResult,
} from '../types';

type Params = Record<string, string | undefined>;

export const adminApi = {
  // Dashboard stats
  getStats: () =>
    api.get<AdminStats>('/admin-panel/stats/').then(r => r.data),

  // Pilot summary (aggregate view for admin/client reviews)
  getPilotSummary: (period: '7d' | '30d' | '90d' | 'all' = '30d') =>
    api.get<AdminPilotSummary>('/admin-panel/pilot-summary/', {
      params: { period },
    }).then(r => r.data),

  // Pilot #2 QA actions (buttons on the Pilot Summary page).
  runSuccessMetrics: () =>
    api.post<any>('/admin-panel/pilot-summary/run-success-metrics/').then(r => r.data),
  runProcessQueuedUploads: () =>
    api.post<{ output: string }>('/admin-panel/pilot-summary/run-process-queued/').then(r => r.data),
  runRematch: (live = false) =>
    api.post<{ mode: string; since: string; output: string }>(
      '/admin-panel/pilot-summary/run-rematch/',
      { live },
    ).then(r => r.data),
  runAggregate: (days = 14) =>
    api.post<{ status: string; message: string }>(
      '/admin-panel/pilot-summary/run-aggregate/',
      { days },
    ).then(r => r.data),
  runScoringCycle: () =>
    api.post<{ status: string; message: string }>(
      '/admin-panel/pilot-summary/run-scoring-cycle/',
    ).then(r => r.data),

  runPilot2Simulation: (vendorIds?: string[]) =>
    api.post<Pilot2SimulationResult>(
      '/admin-panel/pilot-summary/run-simulation/',
      vendorIds ? { vendor_ids: vendorIds } : {},
      // Long-running: pilot #2 simulation copies prod data to sandbox,
      // runs cycles week-by-week.  Give it 3 minutes.
      { timeout: 180000 },
    ).then(r => r.data),

  terminateVendorAtSite: (vendorId: string, siteId: string) =>
    api.post<{
      status: string;
      assignments_closed: number;
      unassigned_date: string;
      message: string;
    }>(
      '/admin-panel/pilot-summary/terminate-vendor-at-site/',
      { vendor_id: vendorId, site_id: siteId },
    ).then(r => r.data),

  // Vendors
  getVendors: (params?: Params) =>
    api.get<PaginatedResponse<OrganizationDetail>>('/admin-panel/vendors/', { params }).then(r => r.data),
  getVendor: (id: string) =>
    api.get<OrganizationDetail>(`/admin-panel/vendors/${id}/`).then(r => r.data),
  updateVendor: (id: string, data: Partial<OrganizationDetail>) =>
    api.patch<OrganizationDetail>(`/admin-panel/vendors/${id}/`, data).then(r => r.data),

  // Clients
  getClients: (params?: Params) =>
    api.get<PaginatedResponse<OrganizationDetail>>('/admin-panel/clients/', { params }).then(r => r.data),
  getClient: (id: string) =>
    api.get<OrganizationDetail>(`/admin-panel/clients/${id}/`).then(r => r.data),
  updateClient: (id: string, data: Partial<OrganizationDetail>) =>
    api.patch<OrganizationDetail>(`/admin-panel/clients/${id}/`, data).then(r => r.data),

  // Sites
  getSites: (params?: Params) =>
    api.get<PaginatedResponse<SiteListItem>>('/admin-panel/sites/', { params }).then(r => r.data),
  getSite: (id: string) =>
    api.get<SiteDetail>(`/admin-panel/sites/${id}/`).then(r => r.data),

  // Guards
  getGuards: (params?: Params) =>
    api.get<PaginatedResponse<GuardListItem>>('/admin-panel/guards/', { params }).then(r => r.data),
  getGuard: (id: string) =>
    api.get<GuardDetail>(`/admin-panel/guards/${id}/`).then(r => r.data),

  // Rules
  getRules: (params?: Params) =>
    api.get<PaginatedResponse<RuleItem>>('/admin-panel/rules/', { params }).then(r => r.data),
  getRule: (id: string) =>
    api.get<RuleDetail>(`/admin-panel/rules/${id}/`).then(r => r.data),
  createRule: (data: Partial<RuleDetail>) =>
    api.post<RuleDetail>('/admin-panel/rules/', data).then(r => r.data),
  updateRule: (id: string, data: Partial<RuleDetail>) =>
    api.patch<RuleDetail>(`/admin-panel/rules/${id}/`, data).then(r => r.data),
  deleteRule: (id: string) =>
    api.delete(`/admin-panel/rules/${id}/`).then(r => r.data),
  toggleRule: (id: string, isActive: boolean) =>
    api.patch<RuleDetail>(`/admin-panel/rules/${id}/toggle/`, { is_active: isActive }).then(r => r.data),
  duplicateRule: (id: string) =>
    api.post<RuleDetail>(`/admin-panel/rules/${id}/duplicate/`).then(r => r.data),
  getRuleVersions: (id: string) =>
    api.get<RuleVersion[]>(`/admin-panel/rules/${id}/versions/`).then(r => r.data),

  // Scoring cycles
  getScoringCycles: (params?: Params) =>
    api.get<PaginatedResponse<ScoringCycle>>('/admin-panel/scoring-cycles/', { params }).then(r => r.data),
  runScoring: () =>
    api.post<ScoringCycle>('/admin-panel/scoring-cycles/run/').then(r => r.data),

  // Rule triggers (audit)
  getRuleTriggers: (params?: Params) =>
    api.get<PaginatedResponse<AdminRuleTriggerLog>>('/admin-panel/rule-triggers/', { params }).then(r => r.data),

  // Feature 7: Confirm/Override/Adjust a pending critical-override trigger.
  reviewTrigger: (
    id: string,
    body: { action: 'confirm' | 'override' | 'adjust'; reason?: string; score_impact_adjusted?: number }
  ) => api.post(`/admin-panel/rule-triggers/${id}/review/`, body).then(r => r.data),

  // Uploads (ingestion)
  getUploads: (params?: Params) =>
    api.get<PaginatedResponse<AdminUploadItem>>('/admin-panel/uploads/', { params }).then(r => r.data),
  getUpload: (id: string) =>
    api.get<AdminUploadItem>(`/admin-panel/uploads/${id}/`).then(r => r.data),

  // Email ingestions
  getEmailIngestions: (params?: Params) =>
    api.get<PaginatedResponse<AdminEmailIngestion>>('/admin-panel/email-ingestions/', { params }).then(r => r.data),
  reprocessEmailIngestion: (id: string) =>
    api.post<{ reprocessed: number }>(`/admin-panel/email-ingestions/${id}/reprocess/`).then(r => r.data),

  // Parsed events (metrics)
  getParsedEvents: (params?: Params) =>
    api.get<PaginatedResponse<AdminParsedEvent>>('/admin-panel/parsed-events/', { params }).then(r => r.data),

  // Patrol plans (read-only display on admin site detail)
  getPatrolPlans: (params?: Params) =>
    api.get<PaginatedResponse<PatrolServicePlan>>('/admin-panel/patrol-plans/', { params }).then(r => r.data),
};
